/**
 * Household health score — one number for the month, with every point shown.
 * The components come straight from the Worker (worker/lib/health.ts); the UI
 * never invents a number it can't attribute.
 */
import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import type { HealthScore } from '../../shared/types';
import { ApiRequestError, api, currentPeriod, money } from '../../lib/api';
import {
  Button,
  Card,
  Disclaimer,
  EmptyState,
  ErrorState,
  Field,
  Loading,
  PanelHeading,
  ProgressBar,
  useResource,
} from './ui';

function band(score: number): { label: string; color: string; note: string } {
  if (score >= 80)
    return {
      label: 'On track',
      color: 'var(--color-ok)',
      note: 'Spending is inside the caps and moving at a sane pace.',
    };
  if (score >= 60)
    return {
      label: 'Worth a look',
      color: 'var(--color-near)',
      note: 'Something is running hot — check the components below.',
    };
  return {
    label: 'Over the line',
    color: 'var(--color-over)',
    note: 'Caps are being blown or the pace is well ahead of the month.',
  };
}

function periodLabel(period: string): string {
  const [y, m] = period.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function ScoreRing({ score, color }: { score: number; color: string }) {
  const radius = 52;
  const circumference = 2 * Math.PI * radius;
  const filled = (Math.max(0, Math.min(100, score)) / 100) * circumference;

  return (
    <svg viewBox="0 0 128 128" className="h-32 w-32 shrink-0" role="img" aria-label={`Score ${score} out of 100`}>
      <circle
        cx="64"
        cy="64"
        r={radius}
        fill="none"
        stroke="var(--color-surface-sunk)"
        strokeWidth="12"
      />
      <circle
        cx="64"
        cy="64"
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth="12"
        strokeLinecap="round"
        strokeDasharray={`${filled} ${circumference - filled}`}
        transform="rotate(-90 64 64)"
      />
      <text
        x="64"
        y="61"
        textAnchor="middle"
        dominantBaseline="middle"
        className="font-display"
        fontSize="30"
        fontWeight="600"
        fill="var(--color-ink)"
      >
        {score}
      </text>
      <text x="64" y="83" textAnchor="middle" fontSize="11" fill="var(--color-ink-faint)">
        of 100
      </text>
    </svg>
  );
}

/**
 * What the API can honestly say about a month. A month with nothing in it has no
 * score — the Worker answers 404 with the reason rather than handing back a
 * fabricated 100 — so "no score yet" is a first-class state here, not an error.
 */
type HealthView =
  | { kind: 'score'; score: HealthScore }
  | { kind: 'none'; reason: 'no_transactions' | 'no_categories'; detail: string };

export default function HealthPanel() {
  const [period, setPeriod] = useState(currentPeriod());

  const load = useCallback(async (): Promise<HealthView> => {
    try {
      return { kind: 'score', score: await api.get<HealthScore>(`/insights/health?period=${period}`) };
    } catch (err) {
      if (err instanceof ApiRequestError && err.status === 404) {
        return {
          kind: 'none',
          reason: err.body.error === 'no_categories' ? 'no_categories' : 'no_transactions',
          detail: err.body.detail ?? '',
        };
      }
      throw err;
    }
  }, [period]);

  const { data, loading, error, reload } = useResource(load, [period]);

  const picker = (
    <Field label="Month" className="w-44">
      <input
        type="month"
        value={period}
        max={currentPeriod()}
        onChange={(e) => e.target.value && setPeriod(e.target.value)}
        className="block min-h-11 w-full rounded-lg border border-border bg-surface px-3 text-base text-ink focus-visible:border-sage focus-visible:ring-2 focus-visible:ring-sage/40 focus-visible:outline-none"
      />
    </Field>
  );

  return (
    <div>
      <PanelHeading
        title="Household health"
        sub="One number for the month, and every point that made it. If you disagree with the score, the components below tell you exactly why it landed there."
        action={picker}
      />

      {loading && <Loading label="Loading the health score" rows={2} />}

      {!loading && error && <ErrorState message={error} onRetry={reload} />}

      {!loading && !error && data?.kind === 'none' && data.reason === 'no_transactions' && (
        <EmptyState
          title={`Nothing to score in ${periodLabel(period)} yet`}
          action={
            <Link
              to="/money/transactions"
              className="inline-flex min-h-11 items-center justify-center rounded-full bg-sage px-4 text-sm font-medium text-white hover:bg-sage/90 focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2 focus-visible:ring-offset-canvas focus-visible:outline-none"
            >
              Log the first transaction
            </Link>
          }
        >
          The score is a mirror of what actually went out the door this month. Nothing is logged for{' '}
          {periodLabel(period)}, so there is no score to show — an empty month is not a perfect month,
          and a fake 100 would be worse than nothing. Log a charge (or pick a month you have already
          keyed in) and the number appears with every point explained.
        </EmptyState>
      )}

      {!loading && !error && data?.kind === 'none' && data.reason === 'no_categories' && (
        <EmptyState
          title="Nothing to score against yet"
          action={
            <Link
              to="/admin"
              className="inline-flex min-h-11 items-center justify-center rounded-full bg-sage px-4 text-sm font-medium text-white hover:bg-sage/90 focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2 focus-visible:ring-offset-canvas focus-visible:outline-none"
            >
              Set up categories
            </Link>
          }
        >
          There is spending in {periodLabel(period)}, but no active budget category with a monthly cap
          to measure it against. Give at least one category a cap in Settings and the score fills in on
          the next load.
        </EmptyState>
      )}

      {!loading && !error && data?.kind === 'score' && (
        <Score health={data.score} onRecompute={reload} />
      )}
    </div>
  );
}

function Score({ health, onRecompute }: { health: HealthScore; onRecompute: () => void }) {
  const { inputs } = health;

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex flex-col items-center gap-5 sm:flex-row sm:items-center">
          <ScoreRing score={health.score} color={band(health.score).color} />
          <div className="text-center sm:text-left">
            <p className="text-sm text-ink-soft">{periodLabel(health.period)}</p>
            <p className="font-display text-2xl font-semibold text-ink">{band(health.score).label}</p>
            <p className="mt-1 max-w-prose text-sm text-ink-soft">{band(health.score).note}</p>
            <p className="mt-3 text-sm text-ink-soft">
              <span className="font-medium text-ink">{money(inputs.total_spent)}</span> spent of{' '}
              <span className="font-medium text-ink">{money(inputs.total_caps)}</span> in caps ·{' '}
              {inputs.categories_over_cap} of {inputs.categories_total}{' '}
              {inputs.categories_total === 1 ? 'category' : 'categories'} over ·{' '}
              {inputs.pace_ratio.toFixed(2)}× pace
            </p>
          </div>
        </div>
      </Card>

      <Card>
        <h3 className="font-display text-lg font-semibold text-ink">How the score was built</h3>
        <ul className="mt-3 space-y-4">
          {inputs.components.map((component) => (
            <li key={component.label}>
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-sm font-medium text-ink">{component.label}</span>
                <span className="text-sm whitespace-nowrap text-ink-soft">
                  <span className="font-medium text-ink">{component.points}</span> / {component.max} pts
                </span>
              </div>
              <div className="mt-1.5">
                <ProgressBar
                  percent={component.max > 0 ? (component.points / component.max) * 100 : 0}
                  color={band(component.max > 0 ? (component.points / component.max) * 100 : 0).color}
                  label={`${component.label}: ${component.points} of ${component.max} points`}
                />
              </div>
              <p className="mt-1.5 text-sm leading-relaxed text-ink-soft">{component.note}</p>
            </li>
          ))}
        </ul>

        <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-border pt-3">
          <Button variant="secondary" onClick={onRecompute}>
            Recompute now
          </Button>
          <p className="text-xs text-ink-faint">
            Last computed {new Date(health.computed_at).toLocaleString()} · also recomputed nightly.
          </p>
        </div>
      </Card>

      <Disclaimer>
        The score is a rough mirror of one month of card spending against caps you set yourself. It is
        not a measure of your financial health, and it is not advice. Anything touching a specific
        investment or tax decision needs a licensed professional.
      </Disclaimer>
    </div>
  );
}
